import { useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";
import { useLazyQuery, useMutation } from "@apollo/client";

import { Button, Card, Filter, PageHeader, TagSize } from "@cofano/cck";

import { Tooltip, Box } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useConfirm } from "components/hooks/useConfirm";
import useGroupedTable from "components/customCompenents/table/useGroupedTable";
import GroupedTable from "components/customCompenents/table/GroupedTable";
import Helmet from "components/helmet/Helmet";

import { DataFormat, Language, usePermission } from "utils";
import { CargoLink, DossierLink, LocationLink, ProductLink, RelationLink, ShipLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";

import StatusChanger from "view/statuses/components/changer/StatusChanger";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";
import CargoActionTypeTag from "view/cargos/components/tags/CargoActionTypeTag";
import LocationSelect, { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import ConnectingVisitField from "view/cargos/components/connectingVisits/ConnectingVisitField";

import { CargoAction } from "./utils/terminalPlanning.types";
import UpdateTerminalPlannigNotesDialog from "./components/dialogs/UpdateTerminalPlannigNotesDialog";

import { CallType, StatusType } from "__generated__/enums";
import { GetPlannedCargoActionsPerCharterDocument } from "__generated__/view/terminalPlanning/graphql/terminalPlanning.queries.generated";
import { UpdateLastTerminalContactDateDocument } from "__generated__/view/terminalPlanning/graphql/terminalPlanning.mutations.generated";

const TerminalPlanning = () => {
	const confirm = useConfirm();
	const routerLocation = useLocation();
	const [ searchParams ] = useSearchParams();

	const locationFromState = useMemo<LocationSelectOption | undefined>(() => {
		if (routerLocation.state?.location) {
			return routerLocation.state.location;
		}

		const locationId = searchParams.get("locationId");
		const locationDescription = searchParams.get("locationDescription");

		if (locationId && locationDescription) {
			return {
				id: Number(locationId),
				description: locationDescription,
			} as LocationSelectOption;
		}

		return undefined;
	}, [ routerLocation.state?.location, searchParams ]);

	const hasAppliedDefaultFilter = useRef(false);

	const [ queryTerminalPlanning, { loading } ] = useLazyQuery(GetPlannedCargoActionsPerCharterDocument);
	const [ updateLastTerminalContactDate ] = useMutation(UpdateLastTerminalContactDateDocument);

	const [ notesDialog, setNotesDialog ] = useState<{ open: boolean; cargoAction: CargoAction | null }>({
		open: false,
		cargoAction: null,
	});

	const session = useStoreState(store => store.session);
	const { isAdmin, isCustomer, isOperator, isReadOnly, isMasterdata, isCompanyUser, isMainUserLimited } = usePermission();
	const canEditLastContactDate = !isReadOnly && (isAdmin || isMasterdata || isCompanyUser || isMainUserLimited);
	const canUpdateNotes = !isReadOnly && (isAdmin || isOperator || isCustomer || isCompanyUser || isMainUserLimited);

	const getStoredTableSize = (fallback: number) => {
		const stored = localStorage.getItem("default-page-size-terminalPlanningTable");
		return stored ? Number(stored) : fallback;
	};

	const storeTableSize = (size: number) => {
		localStorage.setItem("default-page-size-terminalPlanningTable", String(size));
	};

	const defaultSize = getStoredTableSize( 100);

	const { tableProps, filterProps, refetch } = useGroupedTable({
		id: "terminalPlanningTable",
		defaultSize: defaultSize,
		paginationEnabled: true,
		size: "compact",
		onChange: async (pagination, filters) => {
			if (locationFromState && !hasAppliedDefaultFilter.current) {
				return {
					groups: [],
					totalElements: 0,
				};
			}

			storeTableSize(pagination.size);

			const result = await queryTerminalPlanning({
				variables: {
					pagination: {
						page: pagination.page,
						size: pagination.size,
					},
					search: {
						locationIds: filters.find(it => it.property === "locations")?.value?.map((it: LocationSelectOption) => it.id),
					},
				},
			});

			return {
				groups: result.data?.plannedCargoActionsPerCharter.content.map(locationGroup => ({
					id: locationGroup.locationWithGroup.location.id,
					backgroundColor: "#D6E4F1",
					render: () => {
						return (
							<span style={{ display: "flex", alignItems: "center" }}>
								<span style={{ fontWeight: "bold", marginRight: "12px" }}>
									{locationGroup.locationWithGroup.locationGroup?.description.concat(" • ")}{locationGroup.locationWithGroup.location.description}
								</span>
								<FormattedDate date={locationGroup.locationWithGroup.location.lastTerminalContactDate} format="lll" withoutYear />
								{canEditLastContactDate && (
									<Button
										icon={<RefreshIcon fontSize="small" htmlColor={"gray"} />}
										onClick={() => promptUpdateLastTerminalContactDateTimestamp(locationGroup.locationWithGroup.location.id)}
										variant="text"
										size="small">
									</Button>
								)}
							</span>
						);
					},
					subGroups: locationGroup.charterCargoActions.map(charterWithActions => ({
						id: charterWithActions.charter.id,
						backgroundColor: "#eee",
						render: () => {
							return (
								<div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
									<DossierLink charter={charterWithActions.charter} />
									<ShipLink ship={charterWithActions.cargoActions[0].call?.voyage.ship} />
									<RelationLink relation={charterWithActions.charter.debtor} />
								</div>
							);
						},
						rows: charterWithActions.cargoActions,
					})),
				})) ?? [],
				totalElements: result.data?.plannedCargoActionsPerCharter.totalElements || 0,
			};
		},
	});

	useEffect(() => {
		if (!locationFromState || hasAppliedDefaultFilter.current) {
			return;
		}

		hasAppliedDefaultFilter.current = true;

		filterProps.onSearch([
			{
				property: "locations",
				value: [ locationFromState ],
			},
		]);
	}, [ locationFromState, filterProps ]);

	const promptUpdateLastTerminalContactDateTimestamp = async (id: number) => {
		confirm({
			type: "WARNING",
			title: intl.formatMessage({ id: "CONFIRM_UPDATE_TERMINAL_LAST_CONTACT" }),
			confirmText: intl.formatMessage({ id: "YES" }),
		}).then(async () => {
			await updateLastTerminalContactDateTimestamp(id);
		});
	};

	const updateLastTerminalContactDateTimestamp = async (id: number) => {
		await updateLastTerminalContactDate({
			variables: { id },
		});
		refetch();
	};

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("TERMINAL_PLANNING")}
					<Helmet title={intl.formatMessage({ id: "TERMINAL_PLANNING" })} />
				</PageHeader.Title>
			</PageHeader>

			<Box mb={2}>
				<Card disablePadding>
					<Filter
						{...filterProps}
						fields={[ {
							id: "locations",
							title: intl.formatMessage({ id: "LOCATION" }),
							render: (value, onChange) => <LocationSelect onChange={onChange} value={value} multiple />,
						} ]}
					/>
				</Card>
			</Box>

			<Card disablePadding>
				<GroupedTable
					{...tableProps}
					contentLoading={loading}
					getEntityId={row => row.id}
					columns={[ {
						id: "type",
						title: intl.formatMessage({ id: "TYPE" }),
						render: (row) => <CargoActionTypeTag type={row.type ?? CallType.Loading} size={TagSize.SMALL} />,
					}, {
						id: "reference",
						title: intl.formatMessage({ id: "REFERENCE" }),
						render: (row) => row.reference,
					}, {
						id: "status",
						title: intl.formatMessage({ id: "STATUS" }),
						render: (row) => (
							<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
								<StatusChanger
									session={session}
									entity={row.cargo}
									statusType={StatusType.Cargo}
									showHistory={true}
									refetch={refetch} />
							</div>
						),
					}, {
						id: "cargoPlanned",
						title: intl.formatMessage({ id: "CARGO_PLANNED" }),
						render: (row) => <CargoActionDate action={row} />,
					}, {
						id: "cargo",
						title: intl.formatMessage({ id: "CARGO" }),
						render: (row) => <CargoLink cargo={{
							id: row.cargo.id,
							containerNumber: row.cargo.containerNumber ?? undefined,
							cargoNumber: row.cargo.cargoNumber ?? undefined,
							product: row.cargo.product ?? undefined,
						}} />,
					}, {
						id: "product",
						title: intl.formatMessage({ id: "PRODUCT" }),
						render: (row) => <ProductLink product={row.cargo.product} />,
					}, {
						id: "weight",
						title: intl.formatMessage({ id: "WEIGHT" }),
						render: (row) => DataFormat.mtonWeightWithUnit(row.measurements.locationWeight, row.measurements.locationWeightUnit) || DataFormat.tonnage(row.weight),
					}, {
						id: "loadingPlace",
						title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
						render: (row) => {
							const loadingAction = row.type === CallType.Loading ? row : row.firstCorrespondingLoadingAction;
							return <LocationLink location={loadingAction?.location} />;
						},
					}, {
						id: "loadingShip",
						title: "",
						render: (row) => {
							const loadingAction = row.type === CallType.Loading ? row : row.firstCorrespondingLoadingAction;
							return <ConnectingVisitField action={loadingAction} />;
						},
					}, {
						id: "dischargingPlace",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
						render: (row) => {
							const dischargingAction = row.type === CallType.Discharging ? row : row.lastCorrespondingDischargingAction;
							return <LocationLink location={dischargingAction?.location} />;
						},
					}, {
						id: "dischargingShip",
						title: "",
						render: (row) => {
							const dischargingAction = row.type === CallType.Discharging ? row : row.lastCorrespondingDischargingAction;
							return <ConnectingVisitField action={dischargingAction} />;
						},
					}, {
						id: "notes",
						title: intl.formatMessage({ id: "NOTE" }),
						render: (row) => (
							<Box
								onClick={() => {
									if (canUpdateNotes) {
										setNotesDialog({
											open: true,
											cargoAction: row,
										});
									}
								}}
								sx={{
									maxWidth: "250px",
									"&:hover": {
										color: (theme) => theme.palette.primary.main,
										cursor: "pointer",
										textDecoration: "underline",
									},
								}}>
								<Tooltip
									title={row.terminalPlanningNotes || ""}
									placement="left"
									slotProps={{
										tooltip: {
											sx: {
												whiteSpace: "pre-wrap",
											},
										},
									}}>
									<Box
										sx={{
											display: "block",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}>
										{row.terminalPlanningNotes}
									</Box>
								</Tooltip>

								{!row.terminalPlanningNotes && (
									<Box
										sx={{
											borderRadius: "4px",
											border: "2px dashed #d8d9da",
											cursor: "pointer",
											display: "flex",
											flexGrow: 1,
											height: "1rem",
											transition: "border-color 0.3s",
											"&:hover": {
												borderColor: (theme) => theme.palette.primary.main,
											},
										}} />
								)}
							</Box>
						),
					} ]} />
			</Card>

			<UpdateTerminalPlannigNotesDialog
				open={notesDialog.open}
				cargoAction={notesDialog.cargoAction}
				onClose={() => setNotesDialog({ open: false, cargoAction: null })}
				refetch={refetch} />
		</div>
	);
};

export default TerminalPlanning;
